import ActionTypes from './../common/ActionTypes';
import Strings from './../common/Strings';
import { commonActions, uiActions } from './index';
import { getFreshUser, history } from '../helpers';

const firebase = require('firebase/app');
require('firebase/auth');
require('firebase/firestore');
require('firebase/functions');
require('firebase/storage');

function setLoading(loading) {
  return {
    type: ActionTypes.USER_LOADING,
    loading,
  };
}

function setDeletingAccount(isUserDeletingAccount) {
  return {
    type: ActionTypes.USER_DELETING_ACCOUNT,
    isUserDeletingAccount,
  };
}

function setError(error) {
  return {
    type: ActionTypes.USER_ERROR,
    error,
  };
}

function userLoggedIn(user) {
  return {
    type: ActionTypes.USER_LOGGED_IN,
    user,
  };
}

function settingsUpdated(user) {
  return {
    type: ActionTypes.USER_SETTINGS_UPDATED,
    user,
  };
}

function setDeleteAccountError(error) {
  return {
    type: ActionTypes.DELETE_ACCOUNT_ERROR,
    error,
  };
}

function getLoginErrorText(code) {
  if (code) {
    switch (code) {
      case 'auth/invalid-email':
        return Strings.ERROR.loginInvalidEmail;
      case 'auth/user-disabled':
        return Strings.ERROR.loginInvalidEmail;
      case 'auth/user-not-found':
        return Strings.ERROR.loginUserNotFound;
      case 'auth/wrong-password':
        return Strings.ERROR.loginWrongPassword;
      case 'auth/too-many-requests':
        return Strings.ERROR.tooManyAttempts;
      case 'auth/weak-password':
        return Strings.ERROR.weakPassword;
      case 'auth/email-already-in-use':
        return Strings.ERROR.emailAlreadyInUse;
      case 'auth/network-error':
      case 'auth/unknown':
        return Strings.ERROR.noConnection;
      default:
        return Strings.ERROR.defaultBody;
    }
  }

  return Strings.ERROR.defaultBody;
}

function createUser() {
  return dispatch => {
    const { currentUser } = firebase.auth();

    if (currentUser) {
      const user = getFreshUser(currentUser);

      firebase
        .firestore()
        .collection('users')
        .doc(user.uid)
        .set(user, { merge: true })
        .then(() => {
          dispatch(userLoggedIn(user));
          dispatch(setLoading(false));
          dispatch(uiActions.initialized(true));
        })
        .catch(() => {
          dispatch(
            uiActions.showError(
              Strings.ERROR.defaultTitle,
              Strings.ERROR.createUser,
            ),
          );
        });
    }
  };
}

function fetchUser() {
  return dispatch => {
    const { currentUser } = firebase.auth();

    if (currentUser) {
      firebase
        .firestore()
        .collection('users')
        .doc(currentUser.uid)
        .get()
        .then(doc => {
          if (doc.exists) {
            const user = doc.data();
            dispatch(userLoggedIn(user));
            dispatch(setLoading(false));
            dispatch(uiActions.initialized(true));
          } else {
            dispatch(createUser());
          }
        })
        .catch(() => {
          dispatch(
            uiActions.showError(
              Strings.ERROR.defaultTitle,
              Strings.ERROR.defaultBody,
            ),
          );
        });
    }
  };
}

function login(email, password) {
  return dispatch => {
    if (email && password) {
      dispatch(setError(''));
      dispatch(setLoading(true));
      firebase
        .auth()
        .signInWithEmailAndPassword(email, password)
        .catch(error => {
          dispatch(setLoading(false));
          dispatch(setError(getLoginErrorText(error.code)));
        });
    } else {
      dispatch(setError(Strings.ERROR.loginValidation));
    }
  };
}

function sendWelcomeEmail() {
  return () => {
    const { currentUser } = firebase.auth();

    if (currentUser) {
      const httpsCallable = firebase.functions().httpsCallable('welcomeEmail');
      const user = { email: currentUser.email };

      httpsCallable(JSON.stringify(user));
    }
  };
}

function signUp(email, password) {
  return dispatch => {
    if (email && password) {
      dispatch(setError(''));
      dispatch(setLoading(true));
      firebase
        .auth()
        .createUserAndRetrieveDataWithEmailAndPassword(email, password)
        .then(() => {
          // TODO MAKE ONE ?
          // dispatch(sendWelcomeEmail());
        })
        .catch(error => {
          dispatch(setLoading(false));
          dispatch(setError(getLoginErrorText(error.code)));
        });
    } else {
      dispatch(setError(Strings.ERROR.loginValidation));
    }
  };
}

function forgotPassword(email) {
  return dispatch => {
    if (email) {
      dispatch(setError(''));
      dispatch(setLoading(true));
      firebase
        .auth()
        .sendPasswordResetEmail(email)
        .then(() => {
          dispatch(setLoading(false));
        })
        .catch(error => {
          dispatch(setLoading(false));
          dispatch(setError(getLoginErrorText(error.code)));
        });
    } else {
      dispatch(setError(Strings.ERROR.forgotPasswordEmailEmpty));
    }
  };
}

function signOut() {
  return dispatch => {
    dispatch(setLoading(true));
    firebase
      .auth()
      .signOut()
      .then(() => {
        dispatch(setLoading(false));
      })
      .catch(() => {
        dispatch(setLoading(false));
        dispatch(
          uiActions.showError(
            Strings.ERROR.defaultTitle,
            Strings.ERROR.defaultBody,
          ),
        );
      });
  };
}

function doUpdateProfile(dispatch, currentUser, user, onSuccess) {
  const firestore = firebase.firestore();
  const userRef = firestore.collection('users').doc(currentUser.uid);
  firestore
    .runTransaction(async transaction => {
      transaction.set(userRef, user, { merge: true });
    })
    .then(async () => {
      const updatedUser = await userRef.get();
      const data = updatedUser.data();
      dispatch(setLoading(false));
      dispatch(settingsUpdated(data));
      if (onSuccess) {
        onSuccess();
      }
    })
    .catch(() => {
      dispatch(setLoading(false));
      dispatch(
        uiActions.showError(
          Strings.ERROR.defaultTitle,
          Strings.ERROR.defaultBody,
        ),
      );
    });
}

function updateProfile(user, file, onSuccess) {
  return dispatch => {
    const { currentUser } = firebase.auth();

    if (currentUser) {
      dispatch(setLoading(true));

      if (file) {
        const storageRef = firebase.storage().ref();
        const path =
          currentUser.uid + '/company_logo.' + file.name.split('.').pop();
        const companyImageRef = storageRef.child(path);
        companyImageRef
          .put(file)
          .then(snapshot => {
            snapshot.ref
              .getDownloadURL()
              .then(url => {
                user.companyImageURL = url;
                doUpdateProfile(dispatch, currentUser, user, onSuccess);
              })
              .catch(() => {
                dispatch(setLoading(false));
                alert(Strings.ERROR.errorCompanyImage);
              });
          })
          .catch(() => {
            dispatch(setLoading(false));
            alert(Strings.ERROR.errorCompanyImage);
          });
      } else {
        doUpdateProfile(dispatch, currentUser, user, onSuccess);
      }
    }
  };
}

function deleteAccount(password) {
  return dispatch => {
    dispatch(setDeletingAccount(true));
    dispatch(setDeleteAccountError(''));

    const provider = firebase.auth.EmailAuthProvider;
    const credential = provider.credential(
      firebase.auth().currentUser.email,
      password,
    );
    firebase
      .auth()
      .currentUser.reauthenticateAndRetrieveDataWithCredential(credential)
      .then(() => {
        firebase
          .auth()
          .currentUser.getIdToken()
          .then(idToken => {
            const httpsCallable = firebase
              .functions()
              .httpsCallable('deleteStripeAccount');
            httpsCallable(JSON.stringify({ idToken }))
              .then(() => {
                const httpsCallable = firebase
                  .functions()
                  .httpsCallable('deleteAccount');
                httpsCallable(JSON.stringify({ idToken }))
                  .then(() => {
                    dispatch(commonActions.clearStore());
                    dispatch(uiActions.initialized(true));
                    history.replace('login');
                  })
                  .catch(() => {
                    dispatch(setDeletingAccount(false));
                    dispatch(setDeleteAccountError(getLoginErrorText()));
                  });
              })
              .catch(() => {
                dispatch(setDeletingAccount(false));
                dispatch(setDeleteAccountError(getLoginErrorText()));
              });
          })
          .catch(() => {
            dispatch(setDeletingAccount(false));
            dispatch(setDeleteAccountError(getLoginErrorText()));
          });
      })
      .catch(error => {
        dispatch(setDeletingAccount(false));
        dispatch(setDeleteAccountError(getLoginErrorText(error.code)));
      });
  };
}

function resetDeleteAccountError() {
  return dispatch => {
    dispatch(setDeleteAccountError(''));
  };
}

export const userActions = {
  login,
  signOut,
  signUp,
  forgotPassword,
  fetchUser,
  updateProfile,
  deleteAccount,
  resetDeleteAccountError,
  settingsUpdated,
};



// WEBPACK FOOTER //
// ./src/actions/user.js